import { useCallback, useEffect, useRef, useState } from 'react';

import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';

export type OptionsStatus = 'idle' | 'loading' | 'loaded' | 'failed';

export interface PatientsState {
  status: OptionsStatus;
  data: ProtocolPatient[];
  error?: unknown;
}

export interface AssignmentOptions {
  /** Hay de dónde cargar protocolos y pacientes (sin callbacks no se ofrece asignar). */
  available: boolean;
  protocols: Protocol[];
  protocolsStatus: OptionsStatus;
  protocolsError?: unknown;
  retryProtocols: () => void;
  /** Pacientes de un protocolo (en caché por protocolo mientras el paso está abierto). */
  patientsOf: (protocolId: number | undefined) => PatientsState;
  loadPatients: (protocolId: number) => void;
}

const IDLE: PatientsState = { status: 'idle', data: [] };

/**
 * Protocolos (`LoadProtocolsForAssignment`) y pacientes por protocolo (`LoadProtocolPatients`) para la asignación del paso
 * "Revisar ítems" (spec 009, US2). Los protocolos se piden una vez al activarse; los pacientes, al elegir un protocolo.
 */
export const useAssignmentOptions = (
  active: boolean,
  loadProtocols?: () => Promise<Protocol[]>,
  loadPatientsOf?: (protocolId: number) => Promise<ProtocolPatient[]>
): AssignmentOptions => {
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [protocolsStatus, setProtocolsStatus] = useState<OptionsStatus>('idle');
  const [protocolsError, setProtocolsError] = useState<unknown>(undefined);
  const [patients, setPatients] = useState<Record<number, PatientsState>>({});
  const [protocolsToken, setProtocolsToken] = useState(0);
  const alive = useRef(true);
  const requested = useRef<Set<number>>(new Set());

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const callbacks = useRef({ loadProtocols, loadPatientsOf });
  callbacks.current = { loadProtocols, loadPatientsOf };

  const available = !!loadProtocols && !!loadPatientsOf;

  useEffect(() => {
    const load = callbacks.current.loadProtocols;
    // Una vez por asistente; después de un error sólo con "Reintentar" (vuelve a 'idle').
    if (!active || !load || protocolsStatus !== 'idle') return;
    setProtocolsStatus('loading');
    setProtocolsError(undefined);
    load()
      .then((data) => {
        if (!alive.current) return;
        setProtocols(data ?? []);
        setProtocolsStatus('loaded');
      })
      .catch((error: unknown) => {
        if (!alive.current) return;
        setProtocolsError(error);
        setProtocolsStatus('failed');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, protocolsToken]);

  const loadPatients = useCallback((protocolId: number) => {
    const load = callbacks.current.loadPatientsOf;
    if (!load || !protocolId || requested.current.has(protocolId)) return;
    requested.current.add(protocolId);
    setPatients((current) => ({ ...current, [protocolId]: { status: 'loading', data: [] } }));
    load(protocolId)
      .then((data) => {
        if (alive.current) setPatients((current) => ({ ...current, [protocolId]: { status: 'loaded', data: data ?? [] } }));
      })
      .catch((error: unknown) => {
        requested.current.delete(protocolId);
        if (alive.current) setPatients((current) => ({ ...current, [protocolId]: { status: 'failed', data: [], error } }));
      });
  }, []);

  return {
    available,
    protocols,
    protocolsStatus,
    protocolsError,
    retryProtocols: () => {
      setProtocolsStatus('idle');
      setProtocolsToken((value) => value + 1);
    },
    patientsOf: (protocolId) => (protocolId ? patients[protocolId] ?? IDLE : IDLE),
    loadPatients,
  };
};

export default useAssignmentOptions;
